import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  UserCheck, 
  UserX, 
  Eye, 
  Clock, 
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  Download,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Component to preview document with authentication
function DocumentPreview({ documentUrl }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!documentUrl || !documentUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      setLoading(false);
      return;
    }

    const loadImage = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${documentUrl}`, {
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        setImageSrc(blobUrl);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();

    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [documentUrl]);

  if (!documentUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
    return null;
  }

  if (loading) {
    return (
      <div className="mt-4 flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className="mt-4 p-4 bg-red-500/10 rounded-lg text-red-400 text-sm">
        Erro ao carregar preview do documento
      </div>
    );
  }

  return (
    <div className="mt-4">
      <img
        src={imageSrc}
        alt="Documento"
        className="max-h-64 rounded-lg border border-white/10"
      />
    </div>
  );
}

export default function PendingApprovals() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/pending`);
      setPendingUsers(response.data);
    } catch (error) {
      // Error silenced: 'Error fetching pending users:', error);
      toast.error('Erro ao carregar utilizadores pendentes');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async (userId, file) => {
    if (!file) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('document', file);
    
    try {
      const response = await axios.post(`${API_URL}/admin/users/${userId}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento carregado com sucesso');
      
      // Update the user in state
      setSelectedUser(prev => ({...prev, id_document_url: response.data.document_url}));
      setPendingUsers(prev => prev.map(u => 
        u.id === userId ? {...u, id_document_url: response.data.document_url} : u
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  // View document with authentication
  const handleViewDocument = async (documentUrl) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${documentUrl}`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and open it
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up the blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      toast.error('Erro ao abrir documento');
      console.error('Error viewing document:', error);
    }
  };

  // Emit event to notify AdminLayout to refresh counts
  const notifyCountsChanged = () => {
    window.dispatchEvent(new CustomEvent('pendingCountsChanged'));
  };

  const handleApprove = async (userId) => {
    setProcessingId(userId);
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/approve`);
      toast.success('Utilizador aprovado com sucesso');
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      setSelectedUser(null);
      notifyCountsChanged(); // Notify AdminLayout
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar utilizador');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (userId) => {
    const reason = window.prompt('Indique o motivo do rejeição (opcional):');
    if (reason === null) return; // User cancelled
    
    if (!window.confirm('Tem certeza que deseja rejeitar este registo?')) return;
    
    setProcessingId(userId);
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Registo rejeitado e email enviado ao utilizador');
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      setSelectedUser(null);
      notifyCountsChanged(); // Notify AdminLayout
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar utilizador');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pending-approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Clock className="w-7 h-7 text-amber-400" />
            Registos Pendentes
          </h1>
          <p className="text-slate-400 mt-1">
            {pendingUsers.length} {pendingUsers.length === 1 ? 'registo pendente' : 'registos pendentes'} de aprovação
          </p>
        </div>
      </div>

      {pendingUsers.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold text-white mb-2">Tudo em dia!</h3>
          <p className="text-slate-400">Não há registos pendentes de aprovação.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {pendingUsers.map((user) => (
            <div 
              key={user.id}
              className="glass-card p-6 hover:border-blue-500/30 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {user.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {user.phone}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Registado em {formatDate(user.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 md:ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedUser(user)}
                    className="border-white/10 text-slate-300 hover:bg-white/5"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Detalhes
                  </Button>
                  <Button
                    onClick={() => handleApprove(user.id)}
                    disabled={processingId === user.id}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    {processingId === user.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <UserCheck className="w-4 h-4 mr-2" />
                        Aprovar
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleReject(user.id)}
                    disabled={processingId === user.id}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <UserX className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* User Details Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="bg-[#252F38] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-amber-400" />
              Detalhes do Registo
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-6">
              {/* Personal Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <p className="text-xs text-slate-500 mb-1">Nome Completo</p>
                  <p className="text-white font-medium">{selectedUser.name}</p>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <p className="text-xs text-slate-500 mb-1">Email</p>
                  <p className="text-white">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Telefone</p>
                  <p className="text-white">{selectedUser.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">NIF</p>
                  <p className="text-white">{selectedUser.nif || 'Não fornecido'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500 mb-1">Morada</p>
                  <p className="text-white">{selectedUser.address || 'Não fornecida'}</p>
                </div>
              </div>

              {/* Document */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Documento de Identificação</p>
                <div className="bg-[#1D262D] border border-white/10 rounded-xl p-4">
                  {selectedUser.id_document_url ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-400" />
                          <span className="text-white">Documento anexado</span>
                        </div>
                        <button
                          onClick={() => handleViewDocument(selectedUser.id_document_url)}
                          className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ver Documento
                        </button>
                      </div>
                      
                      {/* Preview if image - load with auth */}
                      <DocumentPreview documentUrl={selectedUser.id_document_url} />
                    </>
                  ) : (
                    <div className="flex items-center gap-3 text-amber-400">
                      <AlertCircle className="w-5 h-5" />
                      <span>Sem documento anexado</span>
                    </div>
                  )}
                  
                  {/* Upload button */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <label className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg cursor-pointer transition-colors">
                      {uploadingDoc ? (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                      ) : (
                        <Upload className="w-4 h-4 text-blue-400" />
                      )}
                      <span className="text-blue-400 text-sm">
                        {uploadingDoc ? 'A carregar...' : (selectedUser.id_document_url ? 'Substituir Documento' : 'Carregar Documento')}
                      </span>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,.webp"
                        className="hidden"
                        disabled={uploadingDoc}
                        onChange={(e) => handleUploadDocument(selectedUser.id, e.target.files[0])}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Registration Info */}
              <div className="p-4 bg-white/5 rounded-xl">
                <p className="text-xs text-slate-500">Data de Registo</p>
                <p className="text-white mt-1">{formatDate(selectedUser.created_at)}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Button
                  onClick={() => handleApprove(selectedUser.id)}
                  disabled={processingId === selectedUser.id}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white h-12"
                >
                  {processingId === selectedUser.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <UserCheck className="w-5 h-5 mr-2" />
                      Aprovar Registo
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleReject(selectedUser.id)}
                  disabled={processingId === selectedUser.id}
                  variant="outline"
                  className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 h-12"
                >
                  <UserX className="w-5 h-5 mr-2" />
                  Rejeitar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
